import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, XCircle, AlertCircle, X } from "lucide-react";

// ── Singleton imperativo — funciona en class components y fuera de React ──
let _dispatch = null;
let _id = 0;

export const toast = {
  success: (message, title) => _dispatch?.({ type: "success", message: message ?? "", title }),
  error: (message, title) => _dispatch?.({ type: "error", message: message ?? "", title }),
  info: (message, title) => _dispatch?.({ type: "info", message: message ?? "", title }),
};

const ICONS = { success: CheckCircle, error: XCircle, info: AlertCircle };

const ToastItem = ({ id, type, title, message, exiting, onDismiss }) => {
  const Icon = ICONS[type] || AlertCircle;
  return (
    <div className={`xf-toast xf-toast--${type}${exiting ? " xf-toast--exit" : ""}`}>
      <span className="xf-toast-icon"><Icon size={18} /></span>
      <div className="xf-toast-body">
        {title && <strong className="xf-toast-title">{title}</strong>}
        {message && <span className="xf-toast-msg">{message}</span>}
      </div>
      <button type="button" className="xf-toast-close" onClick={() => onDismiss(id)}>
        <X size={14} />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((t) => t.map((item) => (item.id === id ? { ...item, exiting: true } : item)));
    setTimeout(() => setToasts((t) => t.filter((item) => item.id !== id)), 320);
  }, []);

  const show = useCallback(
    ({ type = "info", message = "", title, duration = 4000 }) => {
      const id = ++_id;
      setToasts((t) => [...t, { id, type, message: message ?? "", title, exiting: false }]);
      if (duration > 0) setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  useEffect(() => {
    _dispatch = show;
    return () => { _dispatch = null; };
  }, [show]);

  return (
    <>
      {children}
      {createPortal(
        <div className="xf-toast-container" aria-live="polite">
          {toasts.map((t) => (
            <ToastItem key={t.id} {...t} onDismiss={dismiss} />
          ))}
        </div>,
        document.body
      )}
    </>
  );
};
